import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';
import { ArrowLeft, ImagePlus, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateOfferPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    currency: 'USD',
    quantity: '',
    category_id: '',
    expiration_date: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (images.length + files.length > 10) {
      toast.error(language === 'ar' ? 'الحد الأقصى 10 صور' : 'Maximum 10 images');
      return;
    }

    setUploading(true);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: ${t('maxFileSize')}`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${API}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setImages(prev => [...prev, { url: data.url, preview: URL.createObjectURL(file) }]);
      } catch {
        toast.error(`${file.name}: ${t('imageUploadError')}`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        currency: formData.currency,
        quantity: parseInt(formData.quantity),
        images: images.map(img => img.url)
      };
      
      await axios.post(`${API}/offers`, submitData);
      toast.success(language === 'ar' ? 'تم إنشاء العرض بنجاح! سيتم مراجعته من قبل الإدارة.' : 'Offer created successfully! It will be reviewed by admin.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating offer:', error);
      toast.error(language === 'ar' ? 'حدث خطأ أثناء إنشاء العرض' : 'Error creating offer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="create-offer-page">
      <AppHeader />

      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6">
          <ArrowLeft size={20} />
          <span>{t('backToDashboard')}</span>
        </Link>

        <div className="mb-8">
          <h1 className="text-4xl font-heading font-bold text-primary mb-2">{t('createNewOffer')}</h1>
          <p className="text-muted-foreground">{t('fillOfferDetails')}</p>
        </div>

        <div className="bg-white p-8 rounded-xl border border-border shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('offerTitle')}</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                data-testid="title-input"
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder={t('offerTitlePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('description')}</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={5}
                data-testid="description-input"
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                placeholder={t('descriptionPlaceholder')}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('price')}</label>
                <div className="flex gap-2">
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    data-testid="currency-select"
                    className="px-3 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent bg-white text-sm font-medium"
                  >
                    <option value="USD">$ USD</option>
                    <option value="IQD">د.ع IQD</option>
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    data-testid="price-input"
                    className="flex-1 px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder={formData.currency === 'IQD' ? '150000' : '100.00'}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('quantity')}</label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                  data-testid="quantity-input"
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('category')}</label>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                required
                data-testid="category-select"
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">{t('selectCategory')}</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{language === 'ar' ? cat.name_ar : cat.name_en}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('imageOptional')} <span className="text-xs text-muted-foreground">({language === 'ar' ? `${images.length}/10` : `${images.length}/10`})</span>
              </label>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleImageUpload}
                multiple
                className="hidden"
                data-testid="image-input"
              />
              {images.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
                  {images.map((img, idx) => (
                    <div key={img.url} className="relative rounded-lg overflow-hidden border border-border aspect-square">
                      <img src={img.preview || `${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        data-testid={`remove-image-${idx}`}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {uploading && (
                <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                  <Loader2 size={16} className="animate-spin" />
                  {language === 'ar' ? 'جاري الرفع...' : 'Uploading...'}
                </div>
              )}
              {images.length < 10 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="add-image-btn"
                  className="w-full h-28 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center gap-1.5 text-muted-foreground hover:border-accent hover:text-accent transition-colors cursor-pointer disabled:opacity-50"
                >
                  <ImagePlus size={28} />
                  <span className="text-sm font-medium">{language === 'ar' ? 'إضافة صور' : 'Add Images'}</span>
                  <span className="text-xs">{t('maxFileSize')}</span>
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('expirationOptional')}</label>
              <input
                type="date"
                name="expiration_date"
                value={formData.expiration_date}
                onChange={handleChange}
                data-testid="expiration-input"
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-offer-btn"
              className="w-full py-3 bg-gradient-to-r from-accent to-accent/90 text-white font-semibold rounded-lg hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 transition-all duration-200"
            >
              {loading ? t('creating') : t('createOffer')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}